import * as stylex from '@stylexjs/stylex';
import { useForm } from '@tanstack/react-form';
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { CatchBoundary, createFileRoute, notFound, redirect } from '@tanstack/react-router';
import {
  forEachObj as rForEachObj,
  isDeepEqual as rIsDeepEqual,
  isDefined as rIsDefined,
  isNot as rIsNot,
  omitBy as rOmitBy,
  pick as rPick,
} from 'remeda';
import {
  number as vNumber,
  parse as vParse,
  pipe as vPipe,
  string as vString,
  transform as vTransform,
} from 'valibot';

import { SecondaryActionsIconButton } from '@/components/secondary-actions-icon-button.component';
import { SESSION_STORAGE } from '@/constants/storage';
import { useFormDraft } from '@/hooks/use-form-draft.hook';
import { CampaignRequestDistributionEnum, CreativeTypeEnum } from '@/libs/openapi';
import {
  Breadcrumb,
  Breadcrumbs,
  HelpCircleStrokeIcon,
  PROGRESS_INDICATOR_MIN_DELAY_DURATION,
  Text,
  TOAST_TIMEOUT,
  minDelay,
  tryCatch,
} from '@/libs/serenity-ui';
import { RouterLink, RouterMenuItemLink } from '@/libs/tanstack-router';
import { auditLogQueries } from '@/resources/audit-log/audit-log.api';
import { campaignRequestMutations, campaignRequestQueries } from '@/resources/campaign-request/campaign-request.api';
import { toApiCampaignRequestFieldAdapter } from '@/resources/campaign-request/campaign-request.field-adapter';
import { campaignRequestPolicies } from '@/resources/campaign-request/campaign-request.policies';
import { campaignRequestCommentPolicies } from '@/resources/campaign-request-comment/campaign-request-comment.policies';
import { organizationTeamPolicies } from '@/resources/organization-team/organization-team.policies';
import { CampaignRequestChatWidget } from '@/routes/_main/-components/campaign-request-chat-widget.component';
import { CampaignRequestForm, type CampaignFormValues } from '@/routes/_main/_form/campaigns/-components/campaign-request-form.component';
import { Resource } from '@/types/resource.type';
import { timezone } from '@/utils/timezone.util';

export const Route = createFileRoute('/_main/_form/campaigns/$campaignId/edit/')({
  component: RouteComponent,
  params: {
    parse: ({ campaignId }) => ({ campaignId: vParse(vPipe(vString(), vTransform(Number), vNumber()), campaignId) }),
    stringify: ({ campaignId }) => ({ campaignId: String(campaignId) }),
  },
  beforeLoad: async ({ context, params }) => {
    if (typeof (params.campaignId as unknown) !== 'number') {
      throw notFound({ routeId: '__root__' });
    }

    if (context.user && !context.can.campaignRequests.view()) {
      throw redirect({ to: '/' });
    }

    return {
      can: {
        [Resource.campaignRequest as const]: rPick(campaignRequestPolicies.bindContext({ user: context.user.original, organization: context.organization?.original }), [ 'create', 'update' ]),
        [Resource.campaignRequestComment as const]: rPick(campaignRequestCommentPolicies.bindContext({ user: context.user.original, organization: context.organization?.original }), [ 'create' ]),
        [Resource.organizationTeam as const]: rPick(organizationTeamPolicies.bindContext({ user: context.user.original }), [ 'create' ]),
      },
      queryOptions: {
        detail: campaignRequestQueries.detail(params.campaignId),
      },
    };
  },
  loader: async ({ context, params }) => {
    if (!context.user) {
      return;
    }

    const detailQueryData = await context.queryClient.ensureQueryData(context.queryOptions.detail);

    if (!context.can.campaignRequest.update(detailQueryData)) {
      throw redirect({ to: '/campaigns/$campaignId', params: { campaignId: params.campaignId }});
    }
  },
  staticData: {
    form: {
      heading: 'Edit',
      toolbarLayout: 'full-width',
      slots: {
        topBarComponent: TopBar,
        actionsComponent: ActionsComponent,
      },
    },
  },
  head: ({ params }) => ({
    meta: [
      {
        title: `TrendMD | Edit campaign ${params.campaignId}`,
      },
    ],
  }),
});

function RouteComponent (): React.ReactElement {
  const routeParams = Route.useParams();
  const navigate = Route.useNavigate();
  const routeContext = Route.useRouteContext();

  const campaignRequestQuery = useSuspenseQuery(routeContext.queryOptions.detail);

  const updateMutation = useMutation(campaignRequestMutations.update);

  const form = useForm({
    defaultValues: {
      campaignName: campaignRequestQuery.data.original.campaignName,
      customId: campaignRequestQuery.data.original.customId ?? '',
      targetingType: campaignRequestQuery.data.original.targetingType,
      npiFile: campaignRequestQuery.data.original.npiFile ? {
        id: campaignRequestQuery.data.original.npiFile.id,
        name: campaignRequestQuery.data.original.npiFile.name,
      } : undefined,
      creativeFile: campaignRequestQuery.data.original.creativeFile ? {
        id: campaignRequestQuery.data.original.creativeFile.id,
        name: campaignRequestQuery.data.original.creativeFile.name,
      } : undefined,
      specialtyTargeting: campaignRequestQuery.data.original.specialtyTargeting ?? '',
      geoTargeting: campaignRequestQuery.data.original.geoTargeting ?? '',
      totalBudget: campaignRequestQuery.data.transformed.forForm.totalBudget,
      cost: campaignRequestQuery.data.transformed.forForm.cost,
      clickGoals: campaignRequestQuery.data.original.clickGoal,
      timezone: campaignRequestQuery.data.original.timezone ?? timezone.get(),
      startDate: campaignRequestQuery.data.meta.startDateRelativeToTimezone?.forFormValue,
      endDate: campaignRequestQuery.data.meta.endDateRelativeToTimezone?.forFormValue,
      distribution: campaignRequestQuery.data.original.distribution,
      monthlyBudget: campaignRequestQuery.data.transformed.forForm.monthlyBudget ?? {},
      ownerUserId: campaignRequestQuery.data.original.owner?.id,
      teamId: campaignRequestQuery.data.original.team?.id,
      ownerSearchInput: '',
      teamSearchInput: '',
    } as CampaignFormValues,
    onSubmit: async ({ value }) => {
      const originalTimezone = campaignRequestQuery.data.original.timezone ?? timezone.get();
      const originalStartDate = campaignRequestQuery.data.meta.startDateRelativeToTimezone?.forFormValue;
      const originalEndDate = campaignRequestQuery.data.meta.endDateRelativeToTimezone?.forFormValue;
      const originalCustomId = campaignRequestQuery.data.original.customId || undefined;
      const originalTotalBudget = campaignRequestQuery.data.transformed.forForm.totalBudget;
      const originalCost = campaignRequestQuery.data.transformed.forForm.cost;
      const originalMonthlyBudget = campaignRequestQuery.data.original.distribution === CampaignRequestDistributionEnum.MONTHLY
        ? campaignRequestQuery.data.transformed.forForm.monthlyBudget
        : undefined;
      const monthlyBudget = value.distribution === CampaignRequestDistributionEnum.MONTHLY
        ? value.monthlyBudget
        : undefined;

      const body = rOmitBy(toApiCampaignRequestFieldAdapter.transform({
        campaignName: value.campaignName !== campaignRequestQuery.data.original.campaignName ? value.campaignName : undefined,
        customId: (value.customId || undefined) !== originalCustomId ? (value.customId || undefined) : undefined,
        targetingType: value.targetingType !== campaignRequestQuery.data.original.targetingType ? value.targetingType : undefined,
        npiFileId: value.npiFile?.id !== campaignRequestQuery.data.original.npiFile?.id ? value.npiFile?.id : undefined,
        creativeFileId: value.creativeFile?.id !== campaignRequestQuery.data.original.creativeFile?.id ? value.creativeFile?.id : undefined,
        specialtyTargeting: (value.specialtyTargeting || undefined) !== (campaignRequestQuery.data.original.specialtyTargeting || undefined)
          ? (value.specialtyTargeting || undefined)
          : undefined,
        geoTargeting: (value.geoTargeting || undefined) !== (campaignRequestQuery.data.original.geoTargeting || undefined)
          ? (value.geoTargeting || undefined)
          : undefined,
        totalBudget: (value.totalBudget ?? 0) !== originalTotalBudget ? (value.totalBudget ?? 0) : undefined,
        cost: (value.cost ?? 0) !== originalCost ? (value.cost ?? 0) : undefined,
        timezone: value.timezone !== originalTimezone ? value.timezone : undefined,
        startDate: value.startDate !== originalStartDate && value.startDate ? value.startDate : undefined,
        endDate: value.endDate !== originalEndDate && value.endDate ? value.endDate : undefined,
        distribution: value.distribution !== campaignRequestQuery.data.original.distribution ? value.distribution : undefined,
        monthlyBudget: !rIsDeepEqual(monthlyBudget, originalMonthlyBudget) ? monthlyBudget : undefined,
        ownerUserId: value.ownerUserId !== campaignRequestQuery.data.original.owner?.id ? value.ownerUserId : undefined,
        teamId: value.teamId !== campaignRequestQuery.data.original.team?.id ? value.teamId : undefined,
        creativeType: CreativeTypeEnum.LINK !== campaignRequestQuery.data.original.creativeType ? CreativeTypeEnum.LINK : undefined,
      }), rIsNot(rIsDefined));

      const response = await tryCatch(minDelay(updateMutation.mutateAsync({
        id: routeParams.campaignId,
        body,
      }), PROGRESS_INDICATOR_MIN_DELAY_DURATION));

      if (response.error) {
        routeContext.toastQueue.add({ message: response.error.message, variant: 'error', actionProps: { children: 'Retry', onPress: () => form.handleSubmit() }}, { timeout: TOAST_TIMEOUT });
        return;
      }

      clearDraft();
      routeContext.queryClient.invalidateQueries(campaignRequestQueries.all);
      routeContext.queryClient.invalidateQueries(auditLogQueries.all);
      routeContext.toastQueue.add({ message: 'Campaign updated.', variant: 'success' }, { timeout: TOAST_TIMEOUT });
      navigate({ to: '/campaigns/$campaignId', params: { campaignId: routeParams.campaignId }});
    },
  });

  const { hasDraft, restoreDraft, clearDraft, cancelDraft } = useFormDraft<CampaignFormValues>({
    storageKey: SESSION_STORAGE.campaignRequestEdit(String(routeParams.campaignId)).key,
    subscribe: (cb) => form.store.subscribe(({ currentVal }) => !currentVal.isDefaultValue && cb(currentVal.values)),
    restoreValues: rForEachObj((value, key) => form.setFieldValue(key, value)),
  });

  return (
    <>
      <CatchBoundary errorComponent={() => null} getResetKey={() => 'reset'} onCatch={(error, errorInfo) => console.error(error, errorInfo)}>
        <CampaignRequestChatWidget
          campaignRequestId={routeParams.campaignId}
          canCreate={routeContext.can.campaignRequestComment.create()}
        />
      </CatchBoundary>

      <CampaignRequestForm
        canUpdate
        cancelDraft={cancelDraft}
        form={form}
        hasDraft={hasDraft}
        isSavePending={false}
        restoreDraft={restoreDraft}
        routeContext={routeContext}
        validationExcludeId={routeParams.campaignId}
      />
    </>
  );
}

function TopBar () {
  const routeParams = Route.useParams();
  const detailQuery = useQuery(campaignRequestQueries.detail(routeParams.campaignId));

  return (
    <Breadcrumbs>
      <Breadcrumb>
        <RouterLink to="/campaigns">Campaigns</RouterLink>
      </Breadcrumb>
      <Breadcrumb>
        <RouterLink params={routeParams} style={breadcrumbStyles.dynamic} to="/campaigns/$campaignId">{detailQuery.data?.original.campaignName}</RouterLink>
      </Breadcrumb>
      <Breadcrumb hidden>
        <RouterLink params={routeParams} to="/campaigns/$campaignId/edit-requests">Edit requests</RouterLink>
      </Breadcrumb>
    </Breadcrumbs>
  );
}

function ActionsComponent () {
  const routeParams = Route.useParams();

  return (
    <SecondaryActionsIconButton id={routeParams.campaignId}>
      <RouterMenuItemLink to="/campaigns/edit/guide">
        <HelpCircleStrokeIcon />
        <Text>Help</Text>
      </RouterMenuItemLink>
    </SecondaryActionsIconButton>
  );
}

const breadcrumbStyles = stylex.create({
  dynamic: {
    display: 'block',
    maxWidth: 200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});
